// agent-ads-api — 代理广告系统 React-Query hooks。
//
// 后端路由组 /gapi/agent/ads*(需 agent role)
// spec: docs/superpowers/specs/2026-06-29-agent-ads-design.md

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@dogdisk/shared'
import type { AdSlotType } from './ad-slot'

// ─── 数据类型 ───────────────────────────────────────────────────────────────

/** GET /gapi/agent/ads 单条广告(含管理字段) */
export type AdItem = {
  id: string
  image_file_info_id: string
  /** 后端 signAdImageURL 已签名的缩略 URL(imgproxy rs:fit:480:0) */
  image_url: string
  target_url: string
  slogan: string
  sort_order: number
  enabled: boolean
}

/** GET /gapi/agent/ads 完整响应体 */
export type AdListResponse = {
  order_mode: 'fixed' | 'random'
  ads: AdItem[]
}

/** GET /gapi/agent/ad/report 单行聚合 */
export type AdReportRow = {
  day: string
  slot_type: string
  clicks: number
}

// ─── Query hooks ────────────────────────────────────────────────────────────

/**
 * useAds — 查询该 agent 该 slot 全部广告(含 disabled),按 sort_order 升序。
 *
 * 返回 {order_mode, ads}:order_mode 是后端已保存的投放模式;ads 是广告列表。
 */
export function useAds(slot: AdSlotType) {
  return useQuery({
    queryKey: ['agent', 'ads', slot] as const,
    queryFn: () => apiClient.get<AdListResponse>('/gapi/agent/ads', { query: { slot } }),
    staleTime: 30_000,
  })
}

/**
 * useAdReport — 查询广告点击报表。
 * enabled: from/to 都非空时才发请求。
 */
export function useAdReport(from: string, to: string) {
  return useQuery({
    queryKey: ['agent', 'ad', 'report', from, to] as const,
    queryFn: () =>
      apiClient.get<AdReportRow[]>('/gapi/agent/ad/report', { query: { from, to } }),
    enabled: Boolean(from && to),
    staleTime: 60_000,
  })
}

// ─── Mutation hooks ─────────────────────────────────────────────────────────

/** useCreateAd — 新建广告 */
export function useCreateAd() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      slot_type: AdSlotType
      image_file_info_id: string
      target_url: string
      slogan: string
    }) => apiClient.post<AdItem>('/gapi/agent/ads', input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['agent', 'ads', vars.slot_type] })
    },
  })
}

/**
 * useUpdateAd — 编辑广告 target_url / enabled。
 * slot: 用于 invalidate 正确的 query cache。
 */
export function useUpdateAd() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      slot: _slot,
      target_url,
      slogan,
      enabled,
    }: {
      id: string
      slot: AdSlotType
      target_url?: string
      slogan?: string
      enabled?: boolean
    }) => {
      const body: Record<string, string | boolean> = {}
      if (target_url !== undefined) body['target_url'] = target_url
      if (slogan !== undefined) body['slogan'] = slogan
      if (enabled !== undefined) body['enabled'] = enabled
      return apiClient.put<AdItem>(`/gapi/agent/ads/${id}`, body)
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['agent', 'ads', vars.slot] })
    },
  })
}

/** useDeleteAd — 删除广告(返回 204 No Content) */
export function useDeleteAd() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string; slot: AdSlotType }) =>
      apiClient.del<undefined>(`/gapi/agent/ads/${id}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['agent', 'ads', vars.slot] })
    },
  })
}

/** useReorderAds — 批量重排序(返回 204 No Content) */
export function useReorderAds() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      slot_type,
      ordered_ids,
    }: {
      slot_type: AdSlotType
      ordered_ids: string[]
    }) =>
      apiClient.put<undefined>('/gapi/agent/ads/reorder', { slot_type, ordered_ids }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['agent', 'ads', vars.slot_type] })
    },
  })
}

/**
 * useSetSlotMode — 设置广告位投放模式(返回 204 No Content)。
 *
 * 成功后调用方同步更新本地 state 即可;ListAds 会在下次 stale 后自动带回新 order_mode。
 */
export function useSetSlotMode() {
  return useMutation({
    mutationFn: ({
      slot_type,
      order_mode,
    }: {
      slot_type: AdSlotType
      order_mode: 'fixed' | 'random'
    }) =>
      apiClient.put<undefined>('/gapi/agent/ads/slot-mode', { slot_type, order_mode }),
  })
}
